import { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { nivelLabelColor } from "@/lib/constants";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { ArrowLeft, Users, Loader2, TrendingUp, Award } from "lucide-react";

export default function AlunoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const back = new URLSearchParams(location.search).get("back");
  const [aluno, setAluno] = useState(null);
  const [linha, setLinha] = useState([]);
  const [resultadoGeral, setResultadoGeral] = useState(null);
  const [oficinasStatus, setOficinasStatus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    setLoading(true);
    setErro(false);
    Promise.all([api.get(`/alunos/${id}`), api.get(`/alunos/${id}/evolucao`)])
      .then(([a, e]) => { setAluno(a.data); setLinha(e.data.linha); setResultadoGeral(e.data.resultado_geral); setOficinasStatus(e.data.oficinas_status || []); })
      .catch(() => setErro(true))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  if (erro || !aluno) return (
    <div className="text-center py-20">
      <p className="text-muted-foreground">Aluno não encontrado ou indisponível.</p>
      <button data-testid="voltar-btn" onClick={() => navigate(back || "/alunos")} className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary hover:underline">
        <ArrowLeft className="h-4 w-4" /> {back ? "Voltar" : "Voltar para Alunos"}
      </button>
    </div>
  );

  const chartData = linha.map((l, i) => ({
    name: l.oficina_nome?.slice(0, 14) || `Of. ${i + 1}`,
    Geral: l.media_geral, Conhecimento: l.conhecimento, Habilidade: l.habilidade, Atitude: l.atitude,
  }));

  return (
    <div>
      <button data-testid="voltar-btn" onClick={() => navigate(back || "/alunos")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="h-4 w-4" /> {back ? "Voltar" : "Alunos"}
      </button>

      <PageHeader title={aluno.nome} subtitle={`${aluno.nome_guerra ? `"${aluno.nome_guerra}" · ` : ""}${aluno.curso_nome} · ${aluno.turma_nome} · ${aluno.matricula || "—"}${aluno.grau_nome ? ` · ${aluno.grau_nome}` : ""}${aluno.instituicao_nome ? ` · ${aluno.instituicao_nome}` : ""}${aluno.unidade_nome ? ` / ${aluno.unidade_nome}` : ""}${aluno.pais_nome ? ` · ${aluno.pais_nome}` : ""}`} />

      {resultadoGeral && (
        <Card className="p-5 bg-card border-border mb-6" data-testid="resultado-geral">
          <div className="flex items-center gap-2 mb-4">
            <Award className="h-5 w-5 text-primary" />
            <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Resultado Geral da Instrução</h2>
          </div>
          {resultadoGeral.media_geral != null ? (
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">Média</div>
                <div className="text-2xl font-heading font-bold" data-testid="rg-media">{resultadoGeral.media_geral.toFixed(2).replace(".", ",")}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono">Nota Final</div>
                <div className="text-2xl font-heading font-bold text-amber-400" data-testid="rg-nota">{resultadoGeral.nota_final.toFixed(2).replace(".", ",")} / 10</div>
              </div>
              <Badge data-testid="rg-situacao" className={resultadoGeral.aprovado ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/40" : "bg-red-500/20 text-red-300 border-red-500/40"}>{resultadoGeral.situacao}</Badge>
              <Badge className={nivelLabelColor(resultadoGeral.nivel_geral?.label)}>{resultadoGeral.nivel_geral?.label}</Badge>
              <span className="text-xs text-muted-foreground font-mono">{resultadoGeral.oficinas_avaliadas} oficina(s) · {resultadoGeral.criterios_avaliados} critério(s) avaliados</span>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Ainda sem avaliação finalizada nesta instrução.</p>
          )}
          {oficinasStatus.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-1.5" data-testid="rg-oficinas">
              {oficinasStatus.map((o) => (
                <Badge key={o.oficina_id} data-testid={`rg-oficina-${o.oficina_id}`}
                  className={o.status === "finalizada" ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" : o.status === "em_andamento" ? "bg-sky-500/15 text-sky-300 border-sky-500/40" : "bg-secondary text-muted-foreground border-border"}>
                  {o.oficina_nome} — {o.status === "finalizada" ? "Finalizada" : o.status === "em_andamento" ? "Em andamento" : "Não realizada"}
                </Badge>
              ))}
            </div>
          )}
        </Card>
      )}

      <Card className="p-5 bg-card border-border mb-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-5 w-5 text-primary" />
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Evolução por oficina</h2>
        </div>
        {chartData.length ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#30363D" />
              <XAxis dataKey="name" stroke="#8B949E" fontSize={11} />
              <YAxis domain={[0, 5]} stroke="#8B949E" fontSize={11} />
              <Tooltip contentStyle={{ background: "#161B22", border: "1px solid #30363D", borderRadius: 8 }} />
              <Legend />
              <Line type="monotone" dataKey="Geral" stroke="#1F6FE5" strokeWidth={3} dot={{ r: 4 }} />
              <Line type="monotone" dataKey="Conhecimento" stroke="#38BDF8" strokeWidth={1.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Habilidade" stroke="#A855F7" strokeWidth={1.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Atitude" stroke="#F59E0B" strokeWidth={1.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <p className="text-sm text-muted-foreground py-8 text-center">Nenhuma avaliação concluída para este aluno ainda.</p>
        )}
      </Card>

      <h2 className="font-heading text-2xl font-bold uppercase tracking-tight mb-3">Histórico</h2>
      <Card className="bg-card border-border divide-y divide-border">
        {linha.length ? linha.map((l) => (
          <button key={l.avaliacao_id} data-testid={`hist-${l.avaliacao_id}`} onClick={() => navigate(`/avaliacoes/${l.avaliacao_id}?back=${encodeURIComponent(`/alunos/${id}${back ? `?back=${encodeURIComponent(back)}` : ""}`)}`)}
            className="w-full flex items-center justify-between gap-3 px-5 py-3.5 hover:bg-secondary/50 transition-colors text-left">
            <div>
              <div className="font-medium">{l.oficina_nome}</div>
              <div className="text-xs text-muted-foreground font-mono">{l.data} · {l.percentual}%</div>
            </div>
            <Badge className={nivelLabelColor(l.nivel_label)}>{l.nivel_label}</Badge>
          </button>
        )) : (
          <div className="px-5 py-10 text-center text-sm text-muted-foreground flex flex-col items-center gap-2">
            <Users className="h-8 w-8 opacity-40" /> Sem avaliações registradas.
          </div>
        )}
      </Card>
    </div>
  );
}
